import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResolveFn } from '@angular/router';
import { catchError, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OnboardingConfig, extractBrandingData } from 'src/app/shared/models/onboarding-config.model';
import { ThemeService } from 'src/app/shared/services/theme/theme.service';
import { BrandingStore } from 'src/app/shared/stores/branding/branding.store';

export const preQualifyBrandingResolver: ResolveFn<OnboardingConfig | null> = () => {
  const http = inject(HttpClient);
  const themeService = inject(ThemeService);
  const brandingStore = inject(BrandingStore);

  const cached = brandingStore.colors();
  if (cached) {
    themeService.applyBrandColors(cached);
    return of(cached);
  }

  // Check sessionStorage cache (survives page refresh on success page)
  try {
    const sessionCache = sessionStorage.getItem('introducerBrandingCache');
    if (sessionCache) {
      const parsed = JSON.parse(sessionCache) as OnboardingConfig;
      if (parsed) {
        themeService.applyBrandColors(parsed);
        const { logoUrl, brandColor } = extractBrandingData(parsed);
        brandingStore.setBranding({ logoUrl, brandColor, colors: parsed });
        return of(parsed);
      }
    }
  } catch (err) {
    console.error('[PreQualify:Branding] Failed to parse branding from sessionStorage', err);
  }

  let businessId: string | null = null;
  try {
    const raw = localStorage.getItem('introducerContext');
    if (raw) {
      businessId = JSON.parse(raw)?.BusinessId ?? null;
    }
  } catch (err) {
    console.error('[PreQualify:Branding] Failed to parse introducer context from localStorage', err);
  }

  if (!businessId) return of(null);

  return http.get<OnboardingConfig>(environment.onboarding.config, {
    headers: { 'smt-target-brokerage': businessId },
  }).pipe(
    tap((colors) => {
      themeService.applyBrandColors(colors);
      const { logoUrl, brandColor } = extractBrandingData(colors);
      brandingStore.setBranding({ logoUrl, brandColor, colors });
    }),
    catchError((err) => {
      console.error('[PreQualify:Branding] Failed to fetch branding from backend', err);
      brandingStore.setBranding({ logoUrl: null, brandColor: null, colors: null });
      return of(null);
    }),
  );
};
